import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Container,
  FormControlLabel,
  Grid,
  InputAdornment,
  Switch,
  TextField,
  Typography
} from "@mui/material";

import { FieldArray, Formik } from "formik";

import ModalLoadingFull from "../../components/modals/ModalLoadingFull";

import { postNotification } from "../../components/utilities/Notifications";
import { cardUtility } from "./CardUtil";
import { handlePagingAll } from "../../components/api/APIUtilities";
import TextFieldSelect from "../../components/utilities/TextFieldSelect";

import ImageUploader, {
  uploadBas64ToS3
} from "../../components/utilities/ImageUploader";
import ModalLoadingMessage from "../../components/modals/ModalLoadingMessage";
import { useConfirm } from "material-ui-confirm";
import { BCPSquarePlus, BCPTrash } from "../../components/icons/bcp/BCPIcons";
import { getData, postData } from "../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../global/environmentVariables";

const SINGLE_GAMETYPE = getEnvironmentVariable("SINGLE_GAMETYPE");
const IS_SHARDBUGS = getEnvironmentVariable("COMPANY_FLAG") === "shard-bugs";

export default function CardAdmin() {
  const { t } = useTranslation();
  const cardUtil = cardUtility();
  const navigate = useNavigate();
  const confirm = useConfirm();

  let { cardId } = useParams();

  const [loading, setLoading] = useState(true);
  const [processingMessage, setProcessingMessage] = useState(false);
  const [card, setCard] = useState(null);
  const [armies, setArmies] = useState(null);

  useEffect(() => {
    const go = async () => {
      try {
        if (cardId) {
          var apiCard = await getData(`/cards/${cardId}`, {
            queryStringParameters: { "expand[]": ["imageFile", "gameSystem"] }
          });
          apiCard.resourceSymbols = apiCard.resourceSymbols
            ? apiCard.resourceSymbols.map((el) => el.id)
            : [];
          apiCard.factions = apiCard.factions
            ? apiCard.factions.map((el) => el.id)
            : [];

          if (!apiCard.cardSet && apiCard.serial) {
            apiCard.cardSet = cardUtil.cardSets.filter(
              (el) => el.serial === apiCard.serial
            )?.[0]?.name;
            if (!apiCard.cardSet) {
              postNotification(
                "error",
                "Unable to determine Card Set and Serial. Please select the correct Card Set and save."
              );
            }
          } else if (apiCard.cardSet) {
            let cardSet = cardUtil.cardSets.filter(
              (el) => el.name === apiCard.cardSet
            )?.[0];
            if (!cardSet) {
              cardSet = cardUtil.cardSets.filter(
                (el) => el.serial === apiCard.cardSet
              )?.[0];
            }
            if (!cardSet && apiCard.serial) {
              cardSet = cardUtil.cardSets.filter(
                (el) => el.serial === apiCard.serial
              )?.[0];
            }
            if (!cardSet) {
              postNotification(
                "error",
                "Unable to determine Card Set and Serial. Please select the correct Card Set and save."
              );
            } else {
              if (cardSet.name !== apiCard.cardSet) {
                postNotification(
                  "info",
                  "Card Set had errors and has been corrected. Please save card to have corrections take affect"
                );
                apiCard.cardSet = cardSet.name;
              }
              if (cardSet.serial !== apiCard.serial) {
                postNotification(
                  "info",
                  "Card Serial had errors and has been corrected. Please save card to have corrections take affect"
                );
                apiCard.serial = cardSet.serial;
              }
            }
          }

          delete apiCard.analytics;
          delete apiCard.cacheRecord;
          delete apiCard.created_at;
          delete apiCard.updated_at;
          delete apiCard.searchName;
          setCard(apiCard);
        } else {
          setCard({});
        }

        var apiArmies = await handlePagingAll(`/armies`, {
          gameType: apiCard?.gameSystem?.code || SINGLE_GAMETYPE
        });
        setArmies(apiArmies);
      } catch (err) {
        postNotification("error", err);
        navigate("/home");
        setLoading(false);
        return;
      }
      setLoading(false);
    };

    go();
    // eslint-disable-next-line
  }, [cardId]);

  return (
    <>
      {!loading && card && (
        <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
          <Formik
            initialValues={card}
            enableReinitialize={true}
            onSubmit={async (values) => {
              setProcessingMessage("saving...");
              try {
                var body;
                var sanitizedValues = {
                  ...values
                };

                if (sanitizedValues.cardImage) {
                  // upload image to s3
                  var newFile = await uploadBas64ToS3(
                    sanitizedValues.cardImage,
                    {
                      path: "cards",
                      public: true
                    }
                  );
                  delete sanitizedValues.cardImage;
                  if (newFile.fileId) {
                    sanitizedValues.imageFileId = newFile.fileId;
                  }
                }

                if (card?.id) {
                  body = {
                    set: sanitizedValues,
                    unset: {}
                  };

                  for (let key of Object.keys(sanitizedValues)) {
                    if (sanitizedValues[key] === "") {
                      delete body.set[key];
                      body.unset[key] = true;
                    }
                  }

                  await postData(`/cards/${card.id}`, {
                    body: body
                  });
                  postNotification(
                    "success",
                    t("notifications.success.cardUpdated")
                  );
                }
              } catch (err) {
                postNotification("error", err);
              }
              setProcessingMessage(false);
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values,
              setFieldValue,
              setFieldTouched,
              submitForm
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container sx={{ gap: 2 }}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      p: 2,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
                    }}
                  >
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <ImageUploader
                          values={values}
                          label={values.name}
                          name="cardImage"
                          setFieldValue={setFieldValue}
                          aspectW={5}
                          aspectY={7}
                          fileId={values.imageFileId}
                          size="medium"
                          defaultUrl={`${getEnvironmentVariable(
                            "SYSTEM_IMAGES_CLOUDFRONT"
                          )}/images/assets/cardback.png`}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          name="name"
                          label="Card Name"
                          value={values.name}
                          variant="outlined"
                          fullWidth
                          margin="dense"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.name && errors.name)}
                          helperText={touched.name && errors.name}
                          required
                        />
                      </Grid>

                      {cardUtil.cardSets?.length > 0 && (
                        <Grid item xs={12}>
                          <TextFieldSelect
                            list={cardUtil.cardSets.map((cardSet) => {
                              return cardSet.name;
                            })}
                            name="cardSet"
                            label="Card Set"
                            value={values.cardSet}
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            setFieldValue={setFieldValue}
                            setFieldTouched={setFieldTouched}
                            onChange={(e) => {
                              let cardSet = !e?.target?.value
                                ? undefined
                                : cardUtil.cardSets.filter(
                                    (el) => el.cardSet === e.target.value
                                  )?.[0];
                              setFieldValue("serial", cardSet?.serial || "");
                              setFieldTouched("name");
                            }}
                            error={Boolean(touched.cardSet && errors.cardSet)}
                            helperText={touched.cardSet && errors.cardSet}
                            required
                          />
                        </Grid>
                      )}

                      {cardUtil.masterKeys?.filter((el) => el.key === "serial")
                        ?.length > 0 && (
                        <Grid item xs={12}>
                          <TextField
                            name="serial"
                            label="Serial"
                            value={values.serial}
                            variant="outlined"
                            fullWidth
                            margin="dense"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(touched.serial && errors.serial)}
                            helperText={touched.serial && errors.serial}
                            required
                            disabled
                          />
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <TextField
                          name="copies"
                          label="Max number of copies"
                          value={values.copies}
                          variant="outlined"
                          fullWidth
                          margin="dense"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.copies && errors.copies)}
                          helperText={touched.copies && errors.copies}
                          type="number"
                        />
                      </Grid>

                      {cardUtil.masterKeys?.filter(
                        (el) => el.key === "cardType"
                      )?.length > 0 && (
                        <>
                          <Grid item xs={12}>
                            <TextFieldSelect
                              list={cardUtil.cardTypes?.map((el) => {
                                return el?.name || el;
                              })}
                              name="cardType"
                              label="Card Type"
                              value={values.cardType}
                              fullWidth
                              variant="outlined"
                              margin="dense"
                              setFieldValue={setFieldValue}
                              setFieldTouched={setFieldTouched}
                              error={Boolean(
                                touched.cardType && errors.cardType
                              )}
                              helperText={touched.cardType && errors.cardType}
                            />
                          </Grid>

                          {cardUtil.cardSubTypes?.[values.cardType]
                            ?.cardSubTypes?.length > 0 && (
                            <Grid item xs={12}>
                              <TextFieldSelect
                                list={
                                  cardUtil.cardSubTypes?.[values.cardType]
                                    ?.cardSubTypes
                                }
                                name="cardSubType"
                                label={
                                  cardUtil.cardSubTypes?.[values.cardType]
                                    ?.cardSubTypeLabel
                                }
                                value={values.cardSubType}
                                fullWidth
                                variant="outlined"
                                margin="dense"
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                                error={Boolean(
                                  touched.cardSubType && errors.cardSubType
                                )}
                                helperText={
                                  touched.cardSubType && errors.cardSubType
                                }
                              />
                            </Grid>
                          )}

                          {cardUtil.cardSubTypes?.[values.cardType]
                            ?.cardSubTypeChildren?.length > 0 && (
                            <Grid item xs={12}>
                              <TextFieldSelect
                                list={
                                  cardUtil.cardSubTypes?.[values.cardType]
                                    ?.cardSubTypeChildren
                                }
                                name="cardSubTypeChild"
                                label={
                                  cardUtil.cardSubTypes?.[values.cardType]
                                    ?.cardSubTypeChildLabel
                                }
                                value={values.cardSubTypeChild}
                                fullWidth
                                variant="outlined"
                                margin="dense"
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                                error={Boolean(
                                  touched.cardSubTypeChild &&
                                    errors.cardSubTypeChild
                                )}
                                helperText={
                                  touched.cardSubTypeChild &&
                                  errors.cardSubTypeChild
                                }
                              />
                            </Grid>
                          )}
                        </>
                      )}

                      {cardUtil.masterKeys?.filter(
                        (el) => el.key === "resourceSymbols"
                      )?.length > 0 && (
                        <Grid item xs={12}>
                          <TextFieldSelect
                            list={armies.map((army) => {
                              return {
                                value: army.id,
                                label: (
                                  <img
                                    alt={army.name}
                                    src={`${getEnvironmentVariable(
                                      "SYSTEM_IMAGES_CLOUDFRONT"
                                    )}/images/assets/${army.name}.png`}
                                    style={{
                                      maxHeight: "24px",
                                      maxWidth: "24px"
                                    }}
                                  />
                                )
                              };
                            })}
                            name="resourceSymbols"
                            label="Resource Symbols"
                            value={values.resourceSymbols}
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            setFieldValue={setFieldValue}
                            setFieldTouched={setFieldTouched}
                            error={Boolean(
                              touched.resourceSymbols && errors.resourceSymbols
                            )}
                            helperText={
                              touched.resourceSymbols && errors.resourceSymbols
                            }
                            multiple
                            clearAll
                          />
                        </Grid>
                      )}

                      {IS_SHARDBUGS &&
                        cardUtil.masterKeys?.filter(
                          (el) => el.key === "factions"
                        )?.length > 0 && (
                          <Grid item xs={12}>
                            <TextFieldSelect
                              list={armies.map((army) => {
                                return {
                                  value: army.id,
                                  label: army.name
                                };
                              })}
                              name="factionId"
                              label="Alliance"
                              value={values.factionId}
                              fullWidth
                              variant="outlined"
                              margin="dense"
                              setFieldValue={setFieldValue}
                              setFieldTouched={setFieldTouched}
                              error={Boolean(
                                touched.factionId && errors.factionId
                              )}
                              helperText={touched.factionId && errors.factionId}
                              clearAll
                            />
                          </Grid>
                        )}

                      {!IS_SHARDBUGS &&
                        cardUtil.masterKeys?.filter(
                          (el) => el.key === "factions"
                        )?.length > 0 && (
                          <Grid item xs={12}>
                            <TextFieldSelect
                              list={armies.map((army) => {
                                return {
                                  value: army.id,
                                  label: army.name
                                };
                              })}
                              name="factions"
                              label="Factions"
                              value={values.factions}
                              fullWidth
                              variant="outlined"
                              margin="dense"
                              setFieldValue={setFieldValue}
                              setFieldTouched={setFieldTouched}
                              error={Boolean(
                                touched.factions && errors.factions
                              )}
                              helperText={touched.factions && errors.factions}
                              multiple
                              clearAll
                            />
                          </Grid>
                        )}

                      {IS_SHARDBUGS && (
                        <>
                          <Grid item xs={12}>
                            <TextField
                              name="dustCost"
                              label="Dust Cost"
                              value={values.dustCost}
                              variant="outlined"
                              fullWidth
                              margin="dense"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(
                                touched.dustCost && errors.dustCost
                              )}
                              helperText={touched.dustCost && errors.dustCost}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              name="devotion"
                              label="Devotion"
                              value={values.devotion}
                              variant="outlined"
                              fullWidth
                              margin="dense"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(
                                touched.devotion && errors.devotion
                              )}
                              helperText={touched.devotion && errors.devotion}
                              type="number"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              name="attack"
                              label="Attack"
                              value={values.attack}
                              variant="outlined"
                              fullWidth
                              margin="dense"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(touched.attack && errors.attack)}
                              helperText={touched.attack && errors.attack}
                              type="number"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              name="defence"
                              label="Defence"
                              value={values.defence}
                              variant="outlined"
                              fullWidth
                              margin="dense"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(touched.defence && errors.defence)}
                              helperText={touched.defence && errors.defence}
                              type="number"
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextFieldSelect
                              list={cardUtil.traits}
                              name="trait"
                              label="Trait"
                              value={values.trait}
                              fullWidth
                              variant="outlined"
                              margin="dense"
                              setFieldValue={setFieldValue}
                              setFieldTouched={setFieldTouched}
                              error={Boolean(touched.trait && errors.trait)}
                              helperText={touched.trait && errors.trait}
                              clearAll
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextFieldSelect
                              list={cardUtil.resists}
                              name="resist"
                              label="Resist"
                              value={values.resist}
                              fullWidth
                              variant="outlined"
                              margin="dense"
                              setFieldValue={setFieldValue}
                              setFieldTouched={setFieldTouched}
                              error={Boolean(touched.resist && errors.resist)}
                              helperText={touched.resist && errors.resist}
                              clearAll
                            />
                          </Grid>
                        </>
                      )}

                      {cardUtil.masterKeys?.filter((el) => el.key === "check")
                        ?.length > 0 && (
                        <Grid item xs={12}>
                          <TextFieldSelect
                            list={cardUtil.check}
                            name="check"
                            label="Check"
                            value={values.check}
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            setFieldValue={setFieldValue}
                            setFieldTouched={setFieldTouched}
                            error={Boolean(touched.check && errors.check)}
                            helperText={touched.check && errors.check}
                            highlightSelected={false}
                            clearAll
                          />
                        </Grid>
                      )}

                      {cardUtil.masterKeys?.filter(
                        (el) => el.key === "difficulty"
                      )?.length > 0 && (
                        <Grid item xs={12}>
                          <TextFieldSelect
                            list={cardUtil.difficulty}
                            name="difficulty"
                            label="Difficulty"
                            value={values.difficulty}
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            setFieldValue={setFieldValue}
                            setFieldTouched={setFieldTouched}
                            error={Boolean(
                              touched.difficulty && errors.difficulty
                            )}
                            helperText={touched.difficulty && errors.difficulty}
                            highlightSelected={false}
                            clearAll
                          />
                        </Grid>
                      )}

                      {cardUtil.masterKeys?.filter(
                        (el) => el.key === "blockMod"
                      )?.length > 0 && (
                        <Grid item xs={12}>
                          <TextFieldSelect
                            list={cardUtil.blockMod}
                            name="blockMod"
                            label="Block Modifier"
                            value={values.blockMod}
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            setFieldValue={setFieldValue}
                            setFieldTouched={setFieldTouched}
                            error={Boolean(touched.blockMod && errors.blockMod)}
                            helperText={touched.blockMod && errors.blockMod}
                            highlightSelected={false}
                            clearAll
                          />
                        </Grid>
                      )}

                      {cardUtil.masterKeys?.filter(
                        (el) => el.key === "blockZone"
                      )?.length > 0 && (
                        <Grid item xs={12}>
                          <TextFieldSelect
                            list={Object.values(cardUtil.zones)}
                            name="blockZone"
                            label="Block Zone"
                            value={values.blockZone}
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            setFieldValue={setFieldValue}
                            setFieldTouched={setFieldTouched}
                            error={Boolean(
                              touched.blockZone && errors.blockZone
                            )}
                            helperText={touched.blockZone && errors.blockZone}
                            highlightSelected={false}
                            clearAll
                          />
                        </Grid>
                      )}

                      {cardUtil.masterKeys?.filter((el) => el.key === "speed")
                        ?.length > 0 && (
                        <Grid item xs={12}>
                          <TextFieldSelect
                            list={cardUtil.speed}
                            name="speed"
                            label="Speed"
                            value={values.speed}
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            setFieldValue={setFieldValue}
                            setFieldTouched={setFieldTouched}
                            error={Boolean(touched.speed && errors.speed)}
                            helperText={touched.speed && errors.speed}
                            highlightSelected={false}
                            clearAll
                          />
                        </Grid>
                      )}

                      {cardUtil.masterKeys?.filter((el) => el.key === "damage")
                        ?.length > 0 && (
                        <Grid item xs={12}>
                          <TextFieldSelect
                            list={cardUtil.damage}
                            name="damage"
                            label="Damage"
                            value={values.damage}
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            setFieldValue={setFieldValue}
                            setFieldTouched={setFieldTouched}
                            error={Boolean(touched.damage && errors.damage)}
                            helperText={touched.damage && errors.damage}
                            highlightSelected={false}
                            clearAll
                          />
                        </Grid>
                      )}

                      {cardUtil.masterKeys?.filter((el) => el.key === "zone")
                        ?.length > 0 && (
                        <Grid item xs={12}>
                          <TextFieldSelect
                            list={Object.values(cardUtil.zones)}
                            name="zone"
                            label="Zone"
                            value={values.zone}
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            setFieldValue={setFieldValue}
                            setFieldTouched={setFieldTouched}
                            error={Boolean(touched.zone && errors.zone)}
                            helperText={touched.zone && errors.zone}
                            highlightSelected={false}
                            clearAll
                          />
                        </Grid>
                      )}

                      {cardUtil.masterKeys?.filter(
                        (el) => el.key === "handSize"
                      )?.length > 0 && (
                        <Grid item xs={12}>
                          <TextFieldSelect
                            list={cardUtil.handSize}
                            name="handSize"
                            label="Hand Size"
                            value={values.handSize}
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            setFieldValue={setFieldValue}
                            setFieldTouched={setFieldTouched}
                            error={Boolean(touched.handSize && errors.handSize)}
                            helperText={touched.handSize && errors.handSize}
                            highlightSelected={false}
                            clearAll
                          />
                        </Grid>
                      )}

                      {cardUtil.masterKeys?.filter((el) => el.key === "health")
                        ?.length > 0 && (
                        <Grid item xs={12}>
                          <TextField
                            name="health"
                            label="Health"
                            value={values.health}
                            variant="outlined"
                            fullWidth
                            margin="dense"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(touched.health && errors.health)}
                            helperText={touched.health && errors.health}
                            type="number"
                          />
                        </Grid>
                      )}

                      {cardUtil.masterKeys?.filter(
                        (el) => el.key === "cardNumber"
                      )?.length > 0 && (
                        <Grid item xs={12}>
                          <TextField
                            name="cardNumber"
                            label="Card Number"
                            value={values.cardNumber}
                            variant="outlined"
                            fullWidth
                            margin="dense"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(
                              touched.cardNumber && errors.cardNumber
                            )}
                            helperText={touched.cardNumber && errors.cardNumber}
                            type="number"
                          />
                        </Grid>
                      )}

                      {cardUtil.masterKeys?.filter((el) => el.key === "rarity")
                        ?.length > 0 && (
                        <Grid item xs={12}>
                          <TextFieldSelect
                            list={cardUtil.rarities.map((rarity) => {
                              return rarity.name;
                            })}
                            name="rarity"
                            label="Rarity"
                            value={values.rarity}
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            setFieldValue={setFieldValue}
                            setFieldTouched={setFieldTouched}
                            error={Boolean(touched.rarity && errors.rarity)}
                            helperText={touched.rarity && errors.rarity}
                            highlightSelected={false}
                            clearAll
                          />
                        </Grid>
                      )}

                      {cardUtil.masterKeys?.filter(
                        (el) => el.key === "illustrator"
                      )?.length > 0 && (
                        <Grid item xs={12}>
                          <TextField
                            name="illustrator"
                            label="Illustrator"
                            value={values.illustrator}
                            variant="outlined"
                            fullWidth
                            margin="dense"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(
                              touched.illustrator && errors.illustrator
                            )}
                            helperText={
                              touched.illustrator && errors.illustrator
                            }
                          />
                        </Grid>
                      )}

                      {cardUtil.masterKeys?.filter(
                        (el) => el.key === "keywords"
                      )?.length > 0 && (
                        <FieldArray
                          name="keywords"
                          render={(arrayHelpers) => (
                            <>
                              {values.keywords?.length > 0 &&
                                values.keywords.map((keyword, index) => {
                                  return (
                                    <Grid key={index} item xs={12}>
                                      <TextField
                                        name={`keywords[${index}]`}
                                        label={`Keyword ${index + 1}`}
                                        value={values.keywords?.[index]}
                                        variant="outlined"
                                        fullWidth
                                        margin="dense"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        error={Boolean(
                                          touched.keywords?.[index] &&
                                            errors.keywords?.[index]
                                        )}
                                        helperText={
                                          touched.keywords?.[index] &&
                                          errors.keywords?.[index]
                                        }
                                        InputProps={{
                                          endAdornment: (
                                            <InputAdornment position="end">
                                              <Button
                                                sx={{ minWidth: "40px" }}
                                                onClick={() => {
                                                  arrayHelpers.remove(index);
                                                }}
                                              >
                                                <BCPTrash />
                                              </Button>
                                            </InputAdornment>
                                          )
                                        }}
                                      />
                                    </Grid>
                                  );
                                })}
                              <Grid item xs={12}>
                                <Button
                                  type="button"
                                  color="inherit"
                                  variant="outlined"
                                  fullWidth
                                  startIcon={<BCPSquarePlus />}
                                  onClick={() => {
                                    arrayHelpers.push("");
                                  }}
                                >
                                  Add Keyword
                                </Button>
                              </Grid>
                            </>
                          )}
                        />
                      )}

                      {cardUtil.masterKeys?.filter(
                        (el) => el.key === "textBox" || el.key === "cardText"
                      )?.length > 0 && (
                        <Grid item xs={12}>
                          <TextField
                            name="textBox"
                            label="Card Text"
                            value={values.textBox}
                            variant="outlined"
                            fullWidth
                            margin="dense"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(touched.textBox && errors.textBox)}
                            helperText={touched.textBox && errors.textBox}
                            multiline
                            rows={6}
                          />
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <FormControlLabel
                          label={
                            <Typography className="MuiFormControlLabel-label">
                              Public Card
                            </Typography>
                          }
                          sx={{
                            ml: 0,
                            width: "100%",
                            "& .MuiFormControlLabel-label": {
                              width: "calc(100% - 58px)"
                            }
                          }}
                          control={
                            <Switch
                              color="primary"
                              checked={values.public}
                              onChange={async (e) => {
                                setFieldValue("public", !values.public);
                              }}
                            />
                          }
                          labelPlacement="start"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FormControlLabel
                          label={
                            <Typography className="MuiFormControlLabel-label">
                              Ban Card
                            </Typography>
                          }
                          sx={{
                            ml: 0,
                            width: "100%",
                            "& .MuiFormControlLabel-label": {
                              width: "calc(100% - 58px)"
                            }
                          }}
                          control={
                            <Switch
                              color="primary"
                              checked={values.banned}
                              onChange={async (e) => {
                                try {
                                  await confirm({
                                    title: `Are you sure?`,
                                    description: `Are you sure you want to ${
                                      values.banned ? "unban" : "ban"
                                    } this card?`,
                                    dialogProps: {
                                      sx: {
                                        zIndex: 99999
                                      }
                                    }
                                  });
                                } catch (err) {
                                  return;
                                }

                                setFieldValue("banned", !values.banned);
                              }}
                            />
                          }
                          labelPlacement="start"
                        />
                      </Grid>

                      <Grid item xs={12} sx={{ mt: 3 }}>
                        <Button
                          variant="contained"
                          color="success"
                          fullWidth
                          type="submit"
                        >
                          Save
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Container>
      )}
      {loading && <ModalLoadingFull />}
      {Boolean(processingMessage) && (
        <ModalLoadingMessage message={processingMessage} open />
      )}
    </>
  );
}
